import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Block as DisableIcon,
  CheckCircle as EnableIcon,
} from '@mui/icons-material'
import { useSKU, useUpdateSKUStatus } from '../../hooks/api/useSKUs'
import { format } from 'date-fns'
import SKUForm from '../../components/forms/SKUForm'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { getCompanyUom } from '../../utils/companyUom'

const SKUDetailsPage = () => {
  const { user } = useSelector((state: RootState) => state.auth)
  const uom = getCompanyUom(user?.company_uom)
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [formOpen, setFormOpen] = useState(false)

  const { data: sku, isLoading, error } = useSKU(id!)
  const updateSKUStatusMutation = useUpdateSKUStatus()

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleEdit = () => {
    setFormOpen(true)
    handleMenuClose()
  }

  const handleToggleStatus = () => {
    if (sku) {
      updateSKUStatusMutation.mutate({
        id: sku._id,
        isActive: sku.is_active === false,
      })
    }
    handleMenuClose()
  }

  const handleFormClose = () => {
    setFormOpen(false)
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !sku) {
    return (
      <Box p={3}>
        <Alert severity="error">
          {error ? 'Failed to load SKU details' : 'SKU not found'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/skus')}
          sx={{ mt: 2 }}
        >
          Back to SKUs
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/skus')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">{sku.name}</Typography>
        </Box>
        <Box>
          <IconButton onClick={handleMenuOpen}>
            <MoreVertIcon />
          </IconButton>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              SKU Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  SKU Code
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontFamily: 'monospace' }}>
                  {sku.sku_code || 'Not assigned'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  SKU Name
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {sku.name}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Packing per {uom.singular}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {sku.packing}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Standard Rate per {uom.singular}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                  Rs {sku.standard_rate.toFixed(2)}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Special Rate per {uom.singular}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold', color: 'success.main' }}>
                  Rs {sku.special_rate.toFixed(2)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Purchase Rate
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold', color: 'warning.dark' }}>
                  Rs {(sku.purchase_rate ?? 0).toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip label={sku.is_active === false ? 'Disabled' : 'Active'} color={sku.is_active === false ? 'default' : 'success'} size="small" />
              </Grid>

            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Additional Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                SKU ID
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {sku._id}
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Company ID
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {sku.company_id}
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Created Date
              </Typography>
              <Typography variant="body2">
                {format(new Date(sku.createdAt), 'dd/MM/yyyy HH:mm')}
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Last Updated
              </Typography>
              <Typography variant="body2">
                {format(new Date(sku.updatedAt), 'dd/MM/yyyy HH:mm')}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1 }} />
          Edit SKU
        </MenuItem>
        <MenuItem
          onClick={handleToggleStatus}
          sx={{ color: sku.is_active === false ? 'success.main' : 'warning.main' }}
        >
          {sku.is_active === false ? (
            <EnableIcon sx={{ mr: 1 }} />
          ) : (
            <DisableIcon sx={{ mr: 1 }} />
          )}
          {sku.is_active === false ? 'Enable SKU' : 'Disable SKU'}
        </MenuItem>
      </Menu>

      <SKUForm
        open={formOpen}
        onClose={handleFormClose}
        sku={sku}
        mode="edit"
      />

    </Box>
  )
}

export default SKUDetailsPage
